import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useInteractions,
  arrow,
} from '@floating-ui/react'
import { NodeSelection } from '@tiptap/pm/state'
import { Editor } from '@tiptap/react'
import { IconCategory, IconType } from '@deepguide-ai/types'
import * as Icons from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTopicAtomsContext } from '@/hooks/useTopicAtomsContext'
import { Button } from '@/components/ui/button'
import { DocIcon } from '@/components/ui/doc-icon'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { EmojiPicker } from '@/components/emoji-picker'
import { PageSearch } from '../link-popover/page-search'
import './button-floating-editor.scss'

interface ButtonFloatingEditorProps {
  editor: Editor
  isOpen: boolean
  onClose: () => void
}

export function ButtonFloatingEditor({
  editor,
  isOpen,
  onClose,
}: ButtonFloatingEditorProps) {
  const [mode, setMode] = React.useState<'edit' | 'search'>('edit')
  const [buttonText, setButtonText] = React.useState('New Button')
  const [variant, setVariant] = React.useState<'primary' | 'secondary'>(
    'primary'
  )
  const [selectedIcon, setSelectedIcon] = React.useState<string | null>(null)
  const [selectedEmoji, setSelectedEmoji] = React.useState<string | null>(null)
  const [iconCategory, setIconCategory] = React.useState<IconCategory>('LUCIDE')
  const [iconType, setIconType] = React.useState<IconType>('REGULAR')
  const [iconColor, setIconColor] = React.useState('#000000')
  const [href, setHref] = React.useState('')
  const [selectedPageTitle, setSelectedPageTitle] = React.useState<
    string | null
  >(null)
  const arrowRef = React.useRef(null)
  const selectionRef = React.useRef<{ from: number; to: number } | null>(null)
  const { allTopicsFlat } = useTopicAtomsContext()
  const nodeRef = React.useRef<{ pos: number; node: any } | null>(null)
  const [selectedDocId, setSelectedDocId] = React.useState<string | null>(null)

  // Check if we're editing an existing button
  const isEditingExistingButton = React.useMemo(() => {
    if (!editor) return false
    const { selection } = editor.state
    const node = selection instanceof NodeSelection ? selection.node : null
    return node?.type.name === 'inlineButton'
  }, [editor])

  React.useEffect(() => {
    if (isOpen) {
      const checkAndMarkButton = () => {
        const { from, to } = editor.state.selection
        selectionRef.current = { from, to }

        const { selection } = editor.state
        const node = selection instanceof NodeSelection ? selection.node : null

        if (node?.type.name === 'inlineButton') {
          // Editing existing button node
          const { text, href, variant, icon } = node.attrs

          // Store the node position and reference for real-time updates
          nodeRef.current = { pos: from, node }

          // Load existing button attributes
          setButtonText(text || 'New Button')
          setVariant(variant || 'primary')
          setHref(href || '')

          // Handle icon/emoji
          if (icon) {
            if (icon.startsWith('emoji:')) {
              setSelectedEmoji(icon.replace('emoji:', ''))
              setSelectedIcon(null)
              setIconCategory('EMOJI')
            } else {
              setSelectedIcon(icon)
              setSelectedEmoji(null)
              setIconCategory('LUCIDE')
            }
          } else {
            // Clear icon selection if button has no icon
            setSelectedIcon(null)
            setSelectedEmoji(null)
          }

          // Find page title if it's an internal link
          if (href) {
            // const topicId = href.replace('/topics/', '')
            // const topic = allTopicsFlat.find((t) => t.id === topicId)
            const docId = href.split('?')[1]?.split('docId=')[1]?.split('&')[0]
            setSelectedDocId(docId || null)
            const topic = allTopicsFlat.find((t) => t.docId === docId)
            setSelectedPageTitle(topic?.title || null)
          }
        } else {
          // Creating new button from selected text
          const selectedText = editor.state.doc.textBetween(from, to, ' ')
          if (selectedText) {
            setButtonText(selectedText)
          } else {
            setButtonText('New Button')
          }
          // Reset other fields for new button
          setVariant('primary')
          setHref('')
          setSelectedIcon(null)
          setSelectedEmoji(null)
          setSelectedPageTitle(null)
        }
      }

      checkAndMarkButton()
      setTimeout(checkAndMarkButton, 100)
    }

    setMode(isEditingExistingButton ? 'edit' : 'edit')
  }, [isEditingExistingButton, isOpen, editor, allTopicsFlat])

  const getReferenceElement = React.useCallback(() => {
    if (!editor || !editor.state) return null

    const { from, to } = editor.state.selection
    const domRange = editor.view.domAtPos(from)
    const domRangeEnd = editor.view.domAtPos(to)

    const range = document.createRange()
    range.setStart(domRange.node, domRange.offset)
    range.setEnd(domRangeEnd.node, domRangeEnd.offset)

    return {
      getBoundingClientRect: () => range.getBoundingClientRect(),
      getClientRects: () => range.getClientRects(),
    }
  }, [editor])

  const referenceElement = React.useMemo(() => {
    if (!isOpen) return null
    return getReferenceElement()
  }, [isOpen, getReferenceElement])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) onClose()
    },
    placement: 'bottom-start',
    middleware: [
      offset(10),
      flip({
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  })

  React.useEffect(() => {
    if (referenceElement) {
      refs.setReference(referenceElement as any)
    }
  }, [referenceElement, refs])

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
  })

  const { getFloatingProps } = useInteractions([dismiss])

  const handleSave = () => {
    // Use the button text as the content
    const content = buttonText || 'Button'

    // Determine the icon value based on selection
    let iconValue = ''
    if (selectedEmoji) {
      iconValue = `emoji:${selectedEmoji}`
    } else if (selectedIcon) {
      iconValue = selectedIcon
    }

    if (isEditingExistingButton && nodeRef.current) {
      // When editing existing button, the real-time updates have already changed it
      // Just ensure final state is saved
      const { pos } = nodeRef.current
      const node = editor.state.doc.nodeAt(pos)
      if (node && node.type.name === 'inlineButton') {
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.setNodeMarkup(pos, null, {
              text: content,
              href: href || '',
              variant,
              icon: iconValue,
            })
            return true
          })
          .run()
      }
    } else {
      // For new button, insert at current position
      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertInlineButton({
          text: content,
          href: href || '',
          variant,
          icon: iconValue,
        })
        .run()
    }

    onClose()
  }

  const handleRemove = () => {
    // For nodes, we need to delete the node
    if (selectionRef.current) {
      const { from, to } = selectionRef.current
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .run()
    }

    onClose()
  }

  if (!isOpen || !editor || !referenceElement) {
    return null
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className='button-floating-editor-container'
      >
        {/* <FloatingArrow ref={arrowRef} context={context} className="button-floating-arrow" /> */}
        {mode === 'search' ? (
          <PageSearch
            className='!w-full'
            initialQuery=''
            onSelect={(url, title) => {
              setHref(url)
              setSelectedDocId(
                url.split('?')[1]?.split('docId=')[1]?.split('&')[0] || null
              )
              setSelectedPageTitle(title || null)
              if (title && !isEditingExistingButton) {
                setButtonText(title)
              }

              // Update the button href in real-time if editing existing button
              if (isEditingExistingButton && nodeRef.current) {
                const { pos } = nodeRef.current
                const node = editor.state.doc.nodeAt(pos)
                if (node && node.type.name === 'inlineButton') {
                  // Use a transaction without focusing
                  const tr = editor.state.tr
                  tr.setNodeMarkup(pos, null, {
                    ...node.attrs,
                    href: url,
                  })
                  editor.view.dispatch(tr)
                }
              }

              setMode('edit')
            }}
            onBack={() => setMode('edit')}
          />
        ) : (
          <div className='button-editor'>
            <div className='flex items-center justify-between border-b border-sidebar-border px-6 py-5'>
              <h3 className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
                Button Settings
              </h3>
              <Button
                variant='ghost'
                size='xs'
                onClick={onClose}
                className='h-8 w-8 text-[#86888F] hover:bg-muted hover:text-[#43464D]'
              >
                <Icons.X className='h-4 w-4' />
              </Button>
            </div>

            <div className='button-editor-section'>
              <div className='space-y-5 px-6 py-6'>
                <div className='space-y-2'>
                  <Label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                    Label
                  </Label>
                  <div className='button-editor-label-row'>
                    <div className='button-editor-icon-picker flex h-9 w-11 shrink-0 items-center justify-center  bg-white'>
                      <EmojiPicker
                        selectedIcon={selectedEmoji || selectedIcon}
                        iconCategory={iconCategory}
                        iconType={iconType}
                        iconColor={iconColor}
                        isPopoverMode={true}
                        onIconChange={(iconName, category, type, color) => {
                          let iconValue = ''
                          if (category === 'EMOJI') {
                            setSelectedEmoji(iconName)
                            setSelectedIcon(null)
                            setIconCategory('EMOJI')
                            iconValue = `emoji:${iconName}`
                          } else {
                            setSelectedIcon(iconName)
                            setSelectedEmoji(null)
                            setIconCategory(category)
                            if (type) setIconType(type)
                            if (color) setIconColor(color)
                            iconValue = iconName
                          }

                          // Update the button icon in real-time if editing existing button
                          if (isEditingExistingButton && nodeRef.current) {
                            const { pos } = nodeRef.current
                            const node = editor.state.doc.nodeAt(pos)
                            if (node && node.type.name === 'inlineButton') {
                              // Use a transaction without focusing
                              const tr = editor.state.tr
                              tr.setNodeMarkup(pos, null, {
                                ...node.attrs,
                                icon: iconValue,
                              })
                              editor.view.dispatch(tr)
                            }
                          }
                        }}
                        onEmojiSelect={(emoji) => {
                          setSelectedEmoji(emoji)
                          setSelectedIcon(null)
                          setIconCategory('EMOJI')

                          // Update the button icon in real-time if editing existing button
                          if (isEditingExistingButton && nodeRef.current) {
                            const { pos } = nodeRef.current
                            const node = editor.state.doc.nodeAt(pos)
                            if (node && node.type.name === 'inlineButton') {
                              // Use a transaction without focusing
                              const tr = editor.state.tr
                              tr.setNodeMarkup(pos, null, {
                                ...node.attrs,
                                icon: `emoji:${emoji}`,
                              })
                              editor.view.dispatch(tr)
                            }
                          }
                        }}
                      />
                    </div>

                    <Input
                      value={buttonText}
                      onChange={(e) => {
                        const newText = e.target.value
                        setButtonText(newText)

                        // Update the button in real-time if editing existing button
                        if (isEditingExistingButton && nodeRef.current) {
                          const { pos } = nodeRef.current
                          const node = editor.state.doc.nodeAt(pos)
                          if (node && node.type.name === 'inlineButton') {
                            // Use a transaction without focusing
                            const tr = editor.state.tr
                            tr.setNodeMarkup(pos, null, {
                              ...node.attrs,
                              text: newText || 'Button',
                            })
                            editor.view.dispatch(tr)
                          }
                        }
                      }}
                      placeholder='Button text'
                      className='button-editor-input !h-9 rounded-md border-[#E3E1DD] bg-transparent px-3 text-[13px] leading-[1.2] font-normal text-[#43464D] placeholder:text-[#85878D]'
                    />
                  </div>
                </div>

                <div className='button-editor-section'>
                  <Label className='mb-1 text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                    Variant
                  </Label>
                  <p className='mb-3 text-[13px] leading-5 text-[#86888F]'>
                    The button will use your site's customization settings.
                  </p>
                  <div className='inline-flex h-10 w-full rounded-md bg-muted p-1'>
                    <RadioGroup
                      value={variant}
                      className='group after:bg-background has-focus-visible:after:border-ring has-focus-visible:after:ring-ring/50 relative inline-grid w-full grid-cols-[1fr_1fr] items-center gap-0 text-sm font-medium after:absolute after:inset-y-0 after:w-1/2 after:rounded-sm after:shadow-xs after:transition-[translate,box-shadow] after:duration-300 after:ease-[cubic-bezier(0.16,1,0.3,1)] has-focus-visible:after:ring-[3px] data-[state=primary]:after:translate-x-0 data-[state=secondary]:after:translate-x-full'
                      onValueChange={(v) => {
                        const newVariant = v as 'primary' | 'secondary'
                        setVariant(newVariant)

                        // Update the button variant in real-time if editing existing button
                        if (isEditingExistingButton && nodeRef.current) {
                          const { pos } = nodeRef.current
                          const node = editor.state.doc.nodeAt(pos)
                          if (node && node.type.name === 'inlineButton') {
                            // Use a transaction without focusing
                            const tr = editor.state.tr
                            tr.setNodeMarkup(pos, null, {
                              ...node.attrs,
                              variant: newVariant,
                            })
                            editor.view.dispatch(tr)
                          }
                        }
                      }}
                      data-state={variant}
                    >
                      <label className='group-data-[state=secondary]:text-muted-foreground/70 relative z-10 inline-flex h-full min-w-8 cursor-pointer items-center justify-center px-2 py-1 text-sm leading-5 font-medium whitespace-nowrap transition-colors select-none'>
                        Primary
                        <RadioGroupItem
                          value='primary'
                          id='primary'
                          className='sr-only'
                        />
                      </label>
                      <label className='group-data-[state=primary]:text-muted-foreground/70 relative z-10 inline-flex h-full min-w-8 cursor-pointer items-center justify-center px-2 py-1 text-sm leading-5 font-medium whitespace-nowrap transition-colors select-none'>
                        Secondary
                        <RadioGroupItem
                          id='secondary'
                          value='secondary'
                          className='sr-only'
                        />
                      </label>
                    </RadioGroup>
                  </div>
                </div>

                <div className='button-editor-section'>
                  <Label className='mb-1 text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                    On click
                  </Label>
                  <p className='mb-3 text-[13px] leading-5 text-[#86888F]'>
                    Your button can point to either a URL, or a page or space in
                    GitBook.
                  </p>
                  <div
                    className='cursor-pointer rounded-md border border-[#E3E1DD] bg-transparent px-2 py-0.5 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]'
                    onClick={() => setMode('search')}
                  >
                    {href ? (
                      <div className='button-editor-link-content h-8'>
                        {selectedPageTitle && selectedDocId ? (
                          <>
                            <DocIcon
                              topic={
                                allTopicsFlat.find(
                                  (t) => t.docId === selectedDocId
                                )!
                              }
                              size={16}
                              className='flex-shrink-0'
                            />
                            <span className='button-editor-link-text'>
                              {selectedPageTitle}
                            </span>
                          </>
                        ) : (
                          <>
                            <Icons.Globe className='text-muted-foreground h-4 w-4 flex-shrink-0' />
                            <span className='button-editor-link-text'>
                              {href}
                            </span>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className='text-muted-foreground flex h-8 w-full items-center gap-2 text-sm leading-5'>
                        <Icons.Link className='h-4 w-4 flex-shrink-0' />
                        Select page or URL
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='flex w-full items-center justify-between border-t border-sidebar-border px-6 pt-[14px] pb-4'>
              {isEditingExistingButton && (
                <Button
                  variant='ghost'
                  size='smIcon'
                  onClick={handleRemove}
                  className='h-9 w-9 text-[#C24646] hover:bg-[#FDECEC] hover:text-[#C24646]'
                >
                  <Icons.Trash2 className='h-4 w-4' />
                </Button>
              )}
              <div className='button-editor-actions'>
                <Button
                  variant='outline'
                  onClick={onClose}
                  size='sm'
                  className='h-9 w-24 rounded-lg border-[#E3E1DD] px-0 text-[13px] text-[#43464D] hover:bg-muted'
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  size='sm'
                  className='h-9 w-[80px] rounded-lg bg-sidebar-primary px-0 text-[13px] text-white hover:bg-[#6f8264]'
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FloatingPortal>
  )
}
